// Adapters: build the engine's TrackInput from a concrete record.
//   fromPHR      — the app's PHR (lab panels by analyte name, vital series, meds, notes)
//   fromDataset  — the physician-demo dataset (functions/demo/diabetes-patient.json)
// Everything date-like is normalised to ISO YYYY-MM-DD here so the engine never
// sees the app's M/D/YYYY strings.
import type { PHR, VitalSeries, Note } from '../types.ts';
import type { MeasureSpec, VitalSource } from './spec.ts';
import { classOf, SCREENING_MATCH } from './spec.ts';
import type { TrackInput, Reading, MedicationCourse, MedicationEvent, HypoEvent, ScreeningEntry, CurrentMedication, CourseSegment } from './collate.ts';

// ── dates ─────────────────────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');
/** M/D/YYYY · YYYY-MM-DD · M/YYYY · YYYY (any may carry a trailing time) → ISO date, or null. */
export function toIso(s: string | undefined | null): string | null {
  if (!s) return null;
  const t = String(s).trim();
  let m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) { const y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; return `${y}-${pad(+m[1])}-${pad(+m[2])}`; }
  m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${pad(+m[2])}-${pad(+m[3])}`;
  m = t.match(/^(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[2]}-${pad(+m[1])}-01`;
  m = t.match(/^(\d{4})$/);
  if (m) return `${m[1]}-01-01`;
  const p = Date.parse(t);
  return Number.isNaN(p) ? null : new Date(p).toISOString().slice(0, 10);
}
const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') { const n = parseFloat(v.replace(/[^0-9.\-]/g, '')); return Number.isFinite(n) ? n : null; }
  return null;
};
const firstNumber = (s: string | null | undefined): number | null => { const m = (s || '').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };

// ── PHR ───────────────────────────────────────────────────────────────────────
const VITAL_KEY: Record<VitalSource, VitalSeries['key'] | null> = {
  bpSystolic: 'bpSystolic', bpDiastolic: 'bpDiastolic', hr: 'hr', rr: 'rr', spo2: 'spo2',
  temperatureC: 'temperature', weightKg: 'weight', bmi: null, waistCm: null,
};
const LB_PER_KG = 2.2046226218, IN_PER_M = 39.3700787;

function vitalReadings(phr: PHR, src: VitalSource): Reading[] {
  const series = (key: VitalSeries['key']) => phr.vitals.find((v) => v.key === key);
  const samples = (key: VitalSeries['key'], convert: (v: number, unit: string) => number) => {
    const s = series(key); if (!s) return [] as Reading[];
    return s.samples.map((x) => ({ iso: toIso(x.date), v: x.value })).filter((x): x is { iso: string; v: number } => !!x.iso && Number.isFinite(x.v))
      .map((x) => ({ date: x.iso, value: convert(x.v, s.unit) }));
  };
  if (src === 'weightKg') return samples('weight', (v, u) => (/kg/i.test(u) ? v : v / LB_PER_KG));
  if (src === 'temperatureC') return samples('temperature', (v, u) => (/c/i.test(u) ? v : ((v - 32) * 5) / 9));
  if (src === 'bmi') {
    const w = samples('weight', (v, u) => (/kg/i.test(u) ? v : v / LB_PER_KG));
    const h = samples('height', (v, u) => (/cm/i.test(u) ? v / 100 : /m$/i.test(u) ? v : v / IN_PER_M)).sort((a, b) => a.date.localeCompare(b.date));
    if (!h.length) return [];
    return w.map((x) => {
      let hm = h[0].value;                                          // latest height measured on or before the weight
      for (const hh of h) if (hh.date <= x.date) hm = hh.value;
      return { date: x.date, value: Math.round((x.value / (hm * hm)) * 10) / 10 };
    });
  }
  const key = VITAL_KEY[src];
  return key ? samples(key, (v) => v) : [];
}

function labReadings(phr: PHR, m: MeasureSpec): Reading[] {
  if (!m.lab) return [];
  const out: Reading[] = [];
  for (const panel of phr.labs) {
    const iso = toIso(panel.date); if (!iso) continue;
    for (const r of panel.results) {
      if (!m.lab.match.test(r.analyte || '')) continue;
      if (m.lab.unit && !m.lab.unit.test(r.unit || '')) continue;
      const v = num(r.value); if (v == null) continue;
      out.push({ date: iso, value: v });
    }
  }
  return out;
}

function medicationsOf(phr: PHR) {
  type Row = { name: string; dose: string; since: string | null; until: string | null; indication: string };
  const rows: Row[] = phr.medications.map((x) => ({ name: x.name || '', dose: x.dose || '', since: toIso(x.since), until: toIso(x.until), indication: x.indication || '' }));
  const byName = new Map<string, Row[]>();
  for (const r of rows) { if (!r.name) continue; const k = r.name.toLowerCase(); byName.set(k, [...(byName.get(k) ?? []), r]); }
  const courses: MedicationCourse[] = [];
  const events: MedicationEvent[] = [];
  const current: CurrentMedication[] = [];
  const today = new Date().toISOString().slice(0, 10);
  for (const list of byName.values()) {
    const segs = list.filter((r) => r.since).sort((a, b) => a.since!.localeCompare(b.since!));
    const name = list[0].name, cls = classOf(name, '');
    const open = list.filter((r) => !r.until || r.until > today);
    if (open.length) { const cur = open.sort((a, b) => (b.since ?? '').localeCompare(a.since ?? ''))[0]; current.push({ name, class: cls, dose: cur.dose, since: cur.since ?? undefined }); }
    if (!segs.length) continue;
    const segments: CourseSegment[] = segs.map((r) => ({ start: r.since!, end: r.until, dose: r.dose }));
    const ended = segments.every((s) => s.end && s.end <= today);
    courses.push({ drug: name, class: cls, start: segments[0].start, end: ended ? segments.map((s) => s.end!).sort().pop()! : null, segments });
    segs.forEach((r, i) => {
      if (i === 0) { events.push({ date: r.since!, drug: name, action: 'start', dose: r.dose || null, reason: r.indication }); return; }
      const a = firstNumber(segs[i - 1].dose), b = firstNumber(r.dose);
      const action: MedicationEvent['action'] = a != null && b != null && a !== b ? (b > a ? 'increase' : 'decrease') : 'change';
      events.push({ date: r.since!, drug: name, action, dose: r.dose || null, reason: r.indication });
    });
    if (ended) { const last = segs[segs.length - 1]; events.push({ date: last.until!, drug: name, action: 'stop', dose: null, reason: last.indication }); }
  }
  return { courses, events, current };
}

function hyposOf(notes: Note[]): HypoEvent[] {
  return notes.filter((n) => /hypoglyc/i.test(n.title || '')).map((n) => {
    const iso = toIso(n.date) ?? '';
    const g = firstNumber((n.title + ' ' + n.body).match(/(\d{2,3})\s*mg\/dL/)?.[1] ?? null) ?? 0;
    const sev = (n.title + ' ' + n.body).match(/level\s*\d/i)?.[0] ?? '';
    const med = n.body.match(/\bon:?\s*([^.\n]+)/i)?.[1]?.trim() ?? '';
    return { date: iso, glucose: g, context: n.body, severity: sev, medication: med };
  }).filter((h) => h.date);
}

function screeningOf(phr: PHR): Record<string, ScreeningEntry[]> {
  const out: Record<string, ScreeningEntry[]> = {};
  for (const [key, re] of Object.entries(SCREENING_MATCH)) {
    const rows: ScreeningEntry[] = [];
    for (const n of phr.notes) if (re.test(n.title || '')) { const d = toIso(n.date); if (d) rows.push({ date: d, result: n.body }); }
    for (const i of phr.imaging) if (re.test(`${i.bodyPart} ${i.modality} ${i.impression}`)) { const d = toIso(i.date); if (d) rows.push({ date: d, result: i.impression }); }
    if (rows.length) out[key] = rows.sort((a, b) => a.date.localeCompare(b.date));
  }
  return out;
}

export function fromPHR(phr: PHR): TrackInput {
  const meds = medicationsOf(phr);
  return {
    readings: (m) => (m.lab ? labReadings(phr, m) : m.vital ? vitalReadings(phr, m.vital) : []),
    currentMedications: meds.current,
    courses: meds.courses,
    events: meds.events,
    hypoglycemia: hyposOf(phr.notes),
    screening: screeningOf(phr),
    conditions: phr.conditions.map((c) => ({ description: c.description, status: c.status, since: c.since })),
  };
}

// ── physician-demo dataset (functions/demo/diabetes-patient.json) ─────────────
/** Minimal structural view of the demo dataset — only what the adapter touches. */
export interface DemoDatasetLike {
  visits: { index: number; date: string; medications: { name: string; class: string; dose: string }[] }[];
  medicationEvents: MedicationEvent[];
  medicationCourses: MedicationCourse[];
  hypoglycemiaEvents: HypoEvent[];
  screening: Record<string, ScreeningEntry[]>;
}
export function valueAt(obj: unknown, path: string): number | null {
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return typeof cur === 'number' && Number.isFinite(cur) ? cur : null;
}
export function fromDataset(ds: DemoDatasetLike): TrackInput {
  const visits = [...ds.visits].sort((a, b) => a.date.localeCompare(b.date));
  const last = visits[visits.length - 1];
  return {
    readings: (m) => {
      if (!m.path) return [];
      const out: Reading[] = [];
      for (const v of visits) { const val = valueAt(v, m.path); if (val != null) out.push({ date: v.date, value: val, ref: v.index }); }
      return out;
    },
    currentMedications: (last?.medications ?? []).map((x) => ({ name: x.name, class: x.class, dose: x.dose })),
    courses: ds.medicationCourses,
    events: ds.medicationEvents,
    hypoglycemia: ds.hypoglycemiaEvents,
    screening: ds.screening,
  };
}
